import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import api, { API_BASE } from '../api';
import { useAuth } from '../context/auth';
import CTACard from '../components/CTACard';
import SuggestedFriends from '../components/SuggestedFriends';
import { isSpecialAvatarUser, SpecialAvatar } from '../components/UserBadge';
import { trackEvent } from '../analytics';

function avatarColor(username) {
  var hash = 0;
  for (var i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  var hue = ((hash % 360) + 360) % 360;
  return 'hsl(' + hue + ', 55%, 55%)';
}

var Container = styled.div`
  max-width: 410px;
  margin: 0 auto;
  padding: 1em 0.75em 0;
`;

var SearchInput = styled.input`
  width: 100%;
  padding: 0.6em 0.75em;
  font-size: 1rem;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }
`;

var ResultsList = styled.div`
  margin-top: 0.75em;
  display: flex;
  flex-direction: column;
`;

var UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
  padding: 0.6em 0;
  border-bottom: 1px solid #eee;
`;

var UserCardLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75em;
  text-decoration: none;
  color: inherit;
  flex: 1;
  min-width: 0;
`;

var SearchFollowButton = styled.button`
  margin-left: auto;
  background: ${function (props) {
    return props.$isFollowing ? 'black' : 'white';
  }};
  color: ${function (props) {
    return props.$isFollowing ? 'white' : 'black';
  }};
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.3rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
`;

var Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

var AvatarPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1em;
  font-weight: 700;
  flex-shrink: 0;
`;

var UserInfo = styled.div`
  min-width: 0;
`;

var Username = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.4;
`;

var Bio = styled.div`
  color: #777;
  font-size: 0.8rem;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var StatusText = styled.div`
  text-align: center;
  color: #999;
  margin-top: 2em;
  font-size: 0.9rem;
`;

var GoogleContactsSection = styled.div`
  margin-top: 1.25em;
  padding: 1em;
  background: white;
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 12px;
`;

var GoogleContactsTitle = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 0.5em;
`;

var GoogleContactsSubtitle = styled.div`
  color: #666;
  font-size: 0.8rem;
  line-height: 1.4;
  margin-bottom: 0.75em;
`;

var ContactsCarousel = styled.div`
  display: flex;
  gap: 0.75em;
  overflow-x: auto;
  padding-bottom: 0.5em;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

var ContactCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 120px;
  min-width: 120px;
  padding: 0.75em 0.5em;
  border: 1px solid #eee;
  border-radius: 12px;
  background: white;
`;

var ContactCardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: inherit;
  gap: 0.3em;
`;

var ContactCardName = styled.div`
  font-weight: 700;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
`;

var ContactCardUsername = styled.div`
  font-size: 0.65rem;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
`;

var ContactFollowButton = styled.button`
  margin-top: 0.4em;
  background: ${function (p) {
    return p.$isFollowing ? 'black' : 'white';
  }};
  color: ${function (p) {
    return p.$isFollowing ? 'white' : 'black';
  }};
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.3rem 0.75rem;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
`;

var GoogleContactsButton = styled.button`
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
`;

function UserSearch() {
  useEffect(function () {
    document.title = 'Find Friends | HeavyRotation';
  }, []);

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var currentUserId = authTokens?.user?.id;
  var isLoggedIn = !!authTokens?.token;

  // Handle redirect return from Google OAuth on mobile
  useEffect(function () {
    var hash = window.location.hash;
    if (!hash || hash.indexOf('access_token') === -1) return;
    var params = new URLSearchParams(hash.substring(1));
    var token = params.get('access_token');
    if (!token) return;
    // Clear the hash so it doesn't persist
    window.history.replaceState(null, '', window.location.pathname);
    setContactsLoading(true);
    api
      .post('/api/users/google-contacts', { accessToken: token })
      .then(function (res) {
        setContactMatches(res.data.users || []);
        setContactsLoading(false);
      })
      .catch(function () {
        setContactsError('Could not fetch contacts. Please try again.');
        setContactsLoading(false);
      });
  }, []);

  var _query = useState('');
  var query = _query[0];
  var setQuery = _query[1];

  var _users = useState([]);
  var users = _users[0];
  var setUsers = _users[1];

  var _loading = useState(false);
  var loading = _loading[0];
  var setLoading = _loading[1];

  var _searched = useState(false);
  var searched = _searched[0];
  var setSearched = _searched[1];

  var _followedIds = useState({});
  var followedIds = _followedIds[0];
  var setFollowedIds = _followedIds[1];

  var _contactMatches = useState(null);
  var contactMatches = _contactMatches[0];
  var setContactMatches = _contactMatches[1];

  var _contactsLoading = useState(false);
  var contactsLoading = _contactsLoading[0];
  var setContactsLoading = _contactsLoading[1];

  var _contactsError = useState(null);
  var contactsError = _contactsError[0];
  var setContactsError = _contactsError[1];

  var inputRef = useRef(null);

  var fetchUsers = useCallback(
    debounce(function (q) {
      setLoading(true);
      api
        .get('/api/users/search?q=' + encodeURIComponent(q))
        .then(function (res) {
          setUsers(res.data.users || []);
          setSearched(true);
          setLoading(false);
        })
        .catch(function () {
          setLoading(false);
        });
    }, 300),
    []
  );

  // Load recent users on mount
  useEffect(function () {
    setLoading(true);
    api
      .get('/api/users/search?q=')
      .then(function (res) {
        setUsers(res.data.users || []);
        setLoading(false);
      })
      .catch(function () {
        setLoading(false);
      });
  }, []);

  // Auto-focus search input
  useEffect(function () {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Fetch followed IDs for logged-in user
  useEffect(
    function () {
      if (!isLoggedIn) return;
      api
        .get('/api/follows')
        .then(function (res) {
          var ids = res.data || [];
          var map = {};
          for (var i = 0; i < ids.length; i++) {
            map[ids[i]] = true;
          }
          setFollowedIds(map);
        })
        .catch(function () {});
    },
    [isLoggedIn]
  );

  function handleFollow(userId) {
    var wasFollowing = !!followedIds[userId];
    if (!wasFollowing) {
      var user = users.find(function (u) {
        return u.id === userId;
      });
      trackEvent('follow_completed', {
        source: 'find_friends',
        target_user: user ? user.username : userId,
      });
    }
    setFollowedIds(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      if (wasFollowing) {
        delete next[userId];
      } else {
        next[userId] = true;
      }
      return next;
    });
    api.post('/api/follows/' + userId).catch(function () {
      setFollowedIds(function (prev) {
        var next = {};
        for (var k in prev) {
          if (prev.hasOwnProperty(k)) next[k] = prev[k];
        }
        if (wasFollowing) {
          next[userId] = true;
        } else {
          delete next[userId];
        }
        return next;
      });
    });
  }

  function handleFindGoogleContacts() {
    if (!window.google || !window.google.accounts) return;
    var clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    var isMobile = window.innerWidth <= 600;
    var tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope:
        'https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/contacts.other.readonly',
      ux_mode: isMobile ? 'redirect' : 'popup',
      redirect_uri: isMobile ? window.location.origin + '/search' : undefined,
      callback: function (tokenResponse) {
        if (tokenResponse.error) {
          setContactsError('Google authorization was cancelled.');
          return;
        }
        setContactsLoading(true);
        setContactsError(null);
        api
          .post('/api/users/google-contacts', { accessToken: tokenResponse.access_token })
          .then(function (res) {
            setContactMatches(res.data.users || []);
            setContactsLoading(false);
          })
          .catch(function () {
            setContactsError('Could not fetch contacts. Please try again.');
            setContactsLoading(false);
          });
      },
    });
    tokenClient.requestAccessToken();
  }

  function handleChange(e) {
    var val = e.target.value;
    setQuery(val);
    fetchUsers(val);
  }

  var showNoResults = searched && !loading && users.length === 0 && query.trim().length >= 2;
  var isSearching = query.trim() !== '';

  return (
    <Container>
      <SearchInput
        ref={inputRef}
        type="text"
        placeholder="Search by name, username, or city..."
        value={query}
        onChange={handleChange}
      />
      {!isSearching && isLoggedIn && (
        <GoogleContactsSection>
          <GoogleContactsTitle>Find friends from Google</GoogleContactsTitle>
          <GoogleContactsSubtitle>
            See which of your Google contacts are already on HeavyRotation.
          </GoogleContactsSubtitle>
          {contactsError && (
            <StatusText style={{ marginTop: 0, color: 'tomato', fontSize: '0.8rem' }}>
              {contactsError}
            </StatusText>
          )}
          {contactsLoading && (
            <StatusText style={{ marginTop: 0 }}>Searching your contacts...</StatusText>
          )}
          {contactMatches === null && !contactsLoading && (
            <GoogleContactsButton onClick={handleFindGoogleContacts}>
              Search Google Contacts
            </GoogleContactsButton>
          )}
          {contactMatches !== null && !contactsLoading && contactMatches.length === 0 && (
            <StatusText style={{ marginTop: 0, fontWeight: 700, fontSize: '0.8rem' }}>
              None of your Google contacts are on HeavyRotation yet.
            </StatusText>
          )}
          {contactMatches !== null &&
            contactMatches.filter(function (u) {
              return !followedIds[u.id] && u.id !== currentUserId;
            }).length > 0 && (
              <ResultsList>
                {contactMatches
                  .filter(function (u) {
                    return !followedIds[u.id] && u.id !== currentUserId;
                  })
                  .map(function (user) {
                    return (
                      <UserCard key={user.id}>
                        <UserCardLink to={'/profile/' + user.username}>
                          {isSpecialAvatarUser(user.username) ? (
                            <SpecialAvatar size={40} />
                          ) : user.profilePictureURL ? (
                            <Avatar src={API_BASE + user.profilePictureURL} alt={user.username} />
                          ) : (
                            <AvatarPlaceholder style={{ background: avatarColor(user.username) }}>
                              {user.username.charAt(0).toUpperCase()}
                            </AvatarPlaceholder>
                          )}
                          <UserInfo>
                            <Username>{user.displayName || user.username}</Username>
                            {user.displayName && <Bio>@{user.username}</Bio>}
                          </UserInfo>
                        </UserCardLink>
                        <SearchFollowButton
                          $isFollowing={false}
                          onClick={function () {
                            handleFollow(user.id);
                          }}
                        >
                          Follow
                        </SearchFollowButton>
                      </UserCard>
                    );
                  })}
              </ResultsList>
            )}
        </GoogleContactsSection>
      )}
      {!isSearching && (
        <div style={{ marginTop: '1em' }}>
          <SuggestedFriends
            onFollow={function (userId) {
              setFollowedIds(function (prev) {
                var next = {};
                for (var k in prev) {
                  if (prev.hasOwnProperty(k)) next[k] = prev[k];
                }
                next[userId] = true;
                return next;
              });
            }}
          />
        </div>
      )}
      {loading && <StatusText>Searching...</StatusText>}
      {showNoResults && (
        <div style={{ marginTop: '1.5em' }}>
          <CTACard
            title="No users found"
            subtitle="Can't find who you're looking for? Invite them to join Heavy Rotation."
            to="/invites"
            buttonText="Invite Friends"
          />
        </div>
      )}
      {!loading && users.length > 0 && (
        <ResultsList>
          {users.map(function (user) {
            var isSelf = user.id === currentUserId;
            return (
              <UserCard key={user.id}>
                <UserCardLink to={'/profile/' + user.username}>
                  {isSpecialAvatarUser(user.username) ? (
                    <SpecialAvatar size={40} />
                  ) : user.profilePictureURL ? (
                    <Avatar src={API_BASE + user.profilePictureURL} alt={user.username} />
                  ) : (
                    <AvatarPlaceholder style={{ background: avatarColor(user.username) }}>
                      {user.username.charAt(0).toUpperCase()}
                    </AvatarPlaceholder>
                  )}
                  <UserInfo>
                    <Username>{user.displayName || user.username}</Username>
                    {user.displayName && <Bio>@{user.username}</Bio>}
                    {user.location && <Bio>{user.location}</Bio>}
                    {user.bio && <Bio>{user.bio}</Bio>}
                  </UserInfo>
                </UserCardLink>
                {isLoggedIn && !isSelf && (
                  <SearchFollowButton
                    $isFollowing={!!followedIds[user.id]}
                    onClick={function () {
                      handleFollow(user.id);
                    }}
                  >
                    {followedIds[user.id] ? 'Following' : 'Follow'}
                  </SearchFollowButton>
                )}
              </UserCard>
            );
          })}
        </ResultsList>
      )}
    </Container>
  );
}

export default UserSearch;
